/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import { mapRemoteResource, useTokenFamilies, type TokenFamilyItem } from "../api/hooks.js";
import { SubscriptionsScreen, type SubscriptionItem } from "../screens/SubscriptionsScreen.js";
import { useTranslation } from "../context/translation.js";
import { parseTokenSemantics } from "../utils/tokenSemantics.js";

export function SubscriptionsRoute(): VNode {
  const { t } = useTranslation();
  const { tokenFamilies, resource, isLoading, deleteTokenFamily } = useTokenFamilies();
  const toItem = (tf: TokenFamilyItem): SubscriptionItem => {
    const parsed = parseTokenSemantics(tf.kind, tf.extraData);
    const semantic = parsed.status === "valid" ? parsed.semantics : undefined;
    const benefitSummary = semantic?.benefitType === "percentage"
      // Translators: Keep the literal percent sign immediately after the
      // numeric placeholder.
      ? t`${semantic.percentage}% off`
      : semantic?.benefitType === "flat"
        ? t`Up to ${semantic.amounts.join(", ")} off`
        : semantic?.benefitType === "free_item"
          ? semantic.priceSelection === "most_expensive"
            ? t`Highest-priced item free`
            : t`Lowest-priced item free`
          : semantic?.benefitType === "none"
            ? t`No redemption benefit`
          : undefined;
    const ruleSummary = semantic?.kind === "discount"
      ? semantic.benefitType === "none"
        ? t`No redemption benefit; earns one token on qualifying orders`
        : semantic.requiredTokens === 1
          ? t`${benefitSummary} for 1 token; earns one on qualifying orders`
          : t`${benefitSummary} for ${semantic.requiredTokens} tokens; earns one on qualifying orders`
      : benefitSummary ?? (parsed.status === "invalid" ? t`Invalid automatic checkout rule` : undefined);
    const selectorSummary = semantic?.benefitType === "none"
      ? undefined
      : semantic?.productSelectors === "*"
        ? t`All merchant purchases`
        : semantic?.productSelectors
          .map((selector) => selector.name)
          .join(", ");
    return {
      id: tf.slug,
      name: tf.name,
      description: tf.description,
      kind: tf.kind,
      canBeUsed: tf.validBefore ? t`Until ${tf.validBefore}` : t`Always`,
      validUntil: tf.validBefore,
      duration: tf.duration || "30d",
      extraData: tf.extraData,
      ruleSummary,
      selectorSummary,
    };
  };

  const items: SubscriptionItem[] = tokenFamilies.map(toItem);
  const itemsResource = mapRemoteResource(resource, (families) =>
    families.map(toItem),
  );

  return (
    <SubscriptionsScreen items={items} itemsResource={itemsResource} isLoading={isLoading} onDeleteItem={deleteTokenFamily} />
  );
}
